import { Clock, PencilSimple } from '@phosphor-icons/react';
import AnimatedNumber from './AnimatedNumber';

/**
 * Minimal lesson budget card — just the essentials.
 *
 * Big fraction (top):    driven / max(bought, driven + planned)
 * Segmented progress bar (gold for driven, sky for planned)
 * One-line "N nog te rijden"
 *
 * Details (extra / gratis / initial / package) are intentionally omitted —
 * they live in the "Wijzig gegevens" form and would clutter the day-to-day view.
 * The consumer already pre-computes packageLessons (after subtracting
 * initial_lessons) and passes it here as `packageLessons`.
 */
function formatLessons(n) {
    if (n === null || n === undefined || Number.isNaN(n)) return '0';
    const num = Number(n);
    return Number.isInteger(num) ? num.toString() : num.toFixed(1).replace('.', ',');
}

export default function LessonBudgetCard({
    packageName,
    packageLessons = 0,
    extraLessons = 0,
    freeLessons = 0,
    driven = 0,
    planned = 0,
    onCorrect,
}) {
    const totalBought = (Number(packageLessons) || 0) + (Number(extraLessons) || 0) + (Number(freeLessons) || 0);
    const totalDriven = Math.max(0, Number(driven) || 0);
    const totalPlanned = Math.max(0, Number(planned) || 0);

    // Auto-expand: keep denominator coherent even when driven or planned pushes past bought.
    const displayTotal = Math.max(totalBought, totalDriven + totalPlanned);
    const remaining = Math.max(0, displayTotal - totalDriven);

    if (totalBought <= 0 && totalDriven <= 0 && totalPlanned <= 0) return null;

    const pctDriven = displayTotal > 0 ? Math.min(100, (totalDriven / displayTotal) * 100) : 0;
    const pctPlanned = displayTotal > 0 ? Math.min(100 - pctDriven, (totalPlanned / displayTotal) * 100) : 0;

    return (
        <div
            className="w-full bg-[#2D3748] rounded-xl border border-[#4A5568] p-4 space-y-3"
            data-testid="lesson-budget-card"
        >
            {/* Header row */}
            <div className="flex items-baseline justify-between gap-3">
                <span className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">
                    Lesbudget
                </span>
                <div className="flex items-center gap-2">
                    {packageName && (
                        <span className="text-[10px] uppercase tracking-wider text-gray-500 truncate max-w-[45%] text-right" title={packageName}>
                            {packageName}
                        </span>
                    )}
                    {onCorrect && (
                        <button
                            type="button"
                            onClick={onCorrect}
                            className="w-6 h-6 rounded-md flex items-center justify-center text-gray-500 hover:text-[#E5B84A] hover:bg-[#D4A537]/10 transition-colors"
                            data-testid="budget-correct-button"
                            aria-label="Lesbudget corrigeren"
                            title="Lesbudget corrigeren"
                        >
                            <PencilSimple size={13} />
                        </button>
                    )}
                </div>
            </div>

            {/* Big X / Y fraction */}
            <div className="flex items-baseline gap-1 leading-none" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <span className="text-4xl font-bold text-white tabular-nums" data-testid="budget-fraction-numerator">
                    <AnimatedNumber value={totalDriven} duration={900} />
                </span>
                <span className="text-3xl text-gray-500 font-light">/</span>
                <span className="text-4xl font-bold text-[#E5B84A] tabular-nums" data-testid="budget-fraction-denominator">
                    <AnimatedNumber value={displayTotal} duration={900} />
                </span>
                <span className="text-sm text-gray-500 ml-2 self-end mb-1">lessen</span>
            </div>

            {/* Segmented progress bar */}
            <div className="h-2 rounded-full bg-[#1A202C] overflow-hidden flex">
                <div
                    className="h-full transition-all duration-700 ease-out"
                    style={{ width: `${pctDriven}%`, background: 'linear-gradient(90deg, #D4A537 0%, #E5B84A 100%)' }}
                />
                <div
                    className="h-full transition-all duration-700 ease-out"
                    style={{ width: `${pctPlanned}%`, background: 'linear-gradient(90deg, #38BDF8 0%, #7DD3FC 100%)' }}
                />
            </div>

            {/* Bottom line: nog te rijden */}
            <div className="flex items-center gap-2 text-sm" data-testid="budget-remaining">
                <Clock size={15} weight="fill" className="text-[#E5B84A]" />
                {remaining > 0 ? (
                    <span className="text-gray-200">
                        <span className="text-[#E5B84A] font-bold tabular-nums text-base">
                            <AnimatedNumber value={remaining} duration={900} />
                        </span>
                        <span className="ml-1">{remaining === 1 ? 'les' : 'lessen'} nog te rijden</span>
                    </span>
                ) : (
                    <span className="text-emerald-300 font-medium">
                        Alle {formatLessons(displayTotal)} lessen zijn gereden ✓
                    </span>
                )}
            </div>
        </div>
    );
}
